/**
 * Zones API Service
 */

import api from './api';
import type { ApiResponse, Zone, ZoneWithStats, ZoneTaskDistribution } from '../types';

export interface CreateZoneParams {
  name: string;
  cityId: number;
  regionId?: number;
  coordinates: number[][][];
  color?: string;
  defaultDriverUids?: string[];
}

export interface UpdateZoneParams {
  name?: string;
  coordinates?: number[][][];
  color?: string;
  defaultDriverUids?: string[];
  isActive?: boolean;
  /** null clears it; leaving it out keeps the current value. */
  routeDriverUid?: string | null;
}

/**
 * Get all zones for a city
 */
export async function getZones(
  cityId: number,
  options?: { includeStats?: boolean; includeInactive?: boolean }
): Promise<Zone[] | ZoneWithStats[]> {
  const queryParams = new URLSearchParams();
  queryParams.set('cityId', cityId.toString());
  if (options?.includeStats) queryParams.set('includeStats', 'true');
  if (options?.includeInactive) queryParams.set('includeInactive', 'true');

  const response = await api.get<ApiResponse<Zone[] | ZoneWithStats[]>>(
    `/zones?${queryParams.toString()}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch zones');
  }
  
  return response.data.data;
}

/**
 * Get a specific zone
 */
export async function getZone(zoneId: string): Promise<Zone> {
  const response = await api.get<ApiResponse<Zone>>(`/zones/${zoneId}`);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch zone');
  }
  
  return response.data.data;
}

/**
 * Create a new zone
 */
export async function createZone(params: CreateZoneParams): Promise<Zone> {
  const response = await api.post<ApiResponse<Zone>>('/zones', params);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to create zone');
  }
  
  return response.data.data;
}

/**
 * Update a zone
 */
export async function updateZone(zoneId: string, params: UpdateZoneParams): Promise<Zone> {
  const response = await api.put<ApiResponse<Zone>>(`/zones/${zoneId}`, params);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to update zone');
  }
  
  return response.data.data;
}

/**
 * Delete a zone
 */
export async function deleteZone(zoneId: string, hard = false): Promise<void> {
  const queryParams = hard ? '?hard=true' : '';
  const response = await api.delete<ApiResponse<void>>(`/zones/${zoneId}${queryParams}`);
  
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete zone');
  }
}

/**
 * Get zone statistics
 */
export async function getZoneStats(zoneId: string): Promise<{
  taskCount: number;
  driverCount: number;
  activeRouteCount: number;
}> {
  const response = await api.get<ApiResponse<{
    taskCount: number;
    driverCount: number;
    activeRouteCount: number;
  }>>(`/zones/${zoneId}/stats`);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch zone stats');
  }
  
  return response.data.data;
}

/**
 * Find zone containing a point
 */
export async function findZoneByPoint(
  lng: number,
  lat: number,
  cityId?: number
): Promise<Zone | null> {
  const response = await api.post<ApiResponse<Zone | null>>('/zones/find-by-point', {
    lng,
    lat,
    cityId,
  });
  
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to find zone');
  }
  
  return response.data.data || null;
}

/**
 * Add driver to zone
 */
export async function addDriverToZone(zoneId: string, driverUid: string): Promise<Zone> {
  const response = await api.post<ApiResponse<Zone>>(`/zones/${zoneId}/drivers`, {
    driverUid,
  });
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to add driver to zone');
  }
  
  return response.data.data;
}

/**
 * Remove driver from zone
 */
export async function removeDriverFromZone(zoneId: string, driverUid: string): Promise<Zone> {
  const response = await api.delete<ApiResponse<Zone>>(
    `/zones/${zoneId}/drivers/${driverUid}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to remove driver from zone');
  }
  
  return response.data.data;
}

/**
 * Get task distribution across zones
 */
export async function getZoneTaskDistribution(
  cityId: number,
  date?: string
): Promise<ZoneTaskDistribution[]> {
  const queryParams = date ? `?date=${date}` : '';
  const response = await api.get<ApiResponse<ZoneTaskDistribution[]>>(
    `/zones/distribution/${cityId}${queryParams}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch zone distribution');
  }
  
  return response.data.data;
}

/**
 * Get H3 hexes that are already assigned to zones
 */
export async function getH3Assigned(
  cityId: number,
  resolution: number
): Promise<{ h3Index: string; zoneId: string; zoneName: string; color: string }[]> {
  const response = await api.get<ApiResponse<{ h3Index: string; zoneId: string; zoneName: string; color: string }[]>>(
    `/zones/h3-assigned?cityId=${cityId}&resolution=${resolution}`
  );
  
  if (!response.data.success || !response.data.data) {
    return [];
  }
  
  return response.data.data;
}
